import { ALL_PLAN_LAYERS, type PlanLayers } from '../editor-model';
import type { Vec3 } from '../math3d';
import { type Scene3D, sceneHome } from '../view3d';
import { type AssemblerMutableState, type AssemblerSelection, displayRooms } from './assemblerState';
import { roomsOnLevel } from './assemblyModel';
import { assemblySceneCenter, buildAssemblyScene3D, RoomSceneCache } from './assemblyScene3d';

/** What a shell shows after an update: the scene, whether to frame it afresh, and where to glide the camera, if anywhere. */
export interface AssemblyView3DFrame {
  scene: Scene3D<AssemblerSelection>;
  reframe: boolean;
  centerOn: Vec3 | null;
}

/**
 * The floor plan view's 3D scene from one update to the next, the same in
 * every shell:
 * - The level's rooms are built around one center that holds while the level
 *   stays on screen, so a gadget drag, a true-up or any other edit that moves
 *   or resizes rooms never shifts the world under the camera.
 * - The camera frames the scene afresh only for a different level, or once a
 *   level that had no rooms gets some.
 * - The camera centers on a room each time it becomes selected and each time
 *   a drag of it ends, at the place it lands, and holds still during the drag.
 *   When the selection clears, it centers on the whole level, as the opening
 *   view does. A centering that comes up while 3D is hidden happens once 3D
 *   shows.
 * - A read-only view shows the selection without the move gadget.
 */
export class AssemblyView3DScene {
  private readonly cache = new RoomSceneCache();
  private level: number | null = null;
  private center: { x: number; y: number } | null = null;
  private selectedId: string | null = null;
  private dragging = false;
  private centering: 'room' | 'level' | null = null;

  /** Take in the latest state; null while the 3D view is hidden, when only the selection is noted. */
  update(st: AssemblerMutableState, { visible, layers = ALL_PLAN_LAYERS }: { visible: boolean; layers?: PlanLayers }): AssemblyView3DFrame | null {
    this.noteState(st);
    if (!visible) return null;
    const rooms = displayRooms(st);
    this.cache.retain(rooms.map(room => room.id));
    const shown = roomsOnLevel(rooms, st.level);
    const reframe = this.level !== st.level || this.center === null;
    if (reframe) {
      this.level = st.level;
      this.center = shown.length ? assemblySceneCenter(shown) : null;
    }
    const built = buildAssemblyScene3D(shown, this.cache, st.selection, layers, this.center ?? undefined);
    const scene = st.readOnly ? { ...built, gadget: undefined } : built;
    return { scene, reframe, centerOn: this.takeCentering(scene) };
  }

  /** Note a change of selection, and a drag that ended with a room selected: each calls for a centering. */
  private noteState({ selection, drag }: AssemblerMutableState): void {
    const selectedId = selection.kind === 'room' ? selection.id : null;
    if (selectedId !== this.selectedId) {
      this.selectedId = selectedId;
      this.centering = selectedId === null ? 'level' : 'room';
    }
    const dragEnded = this.dragging && drag === null;
    this.dragging = drag !== null;
    if (dragEnded && selectedId !== null) this.centering = 'room';
  }

  /** The point a centering due now glides to: the selected room's center, or the whole level's, as the opening view frames it. */
  private takeCentering(scene: Scene3D<AssemblerSelection>): Vec3 | null {
    const point = this.centering === 'room' ? scene.selectionCenter : this.centering === 'level' ? sceneHome(scene).center : undefined;
    if (!point) return null;
    this.centering = null;
    return point;
  }
}
